import { useCallback, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppLayout from "../layouts/AppLayout.jsx";
import EmailCard from "../components/EmailCard.jsx";
import EmailPrompt from "../components/EmailPrompt.jsx";
import NavEmailMarketing from "../components/NavEmailMarketing.jsx";
import Filters from "../components/Filters.jsx";
import API from "../config/api.js";

export default function Emails() {
  const location = useLocation();
  const navigate = useNavigate();

  const isMyDrafts = location.pathname.includes("emails-drafts");

  const [emails, setEmails]               = useState([]);
  const [categories, setCategories]       = useState([]);
  const [activeCategories, setActiveCategories] = useState([]);
  const [isNew, setIsNew]                 = useState(false);
  const [page, setPage]                   = useState(1);
  const [hasMore, setHasMore]             = useState(true);
  const [loading, setLoading]             = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  // ── Fetch categories (only for Email Vault, not My Drafts) ────────────────
  const fetchCategories = useCallback(async () => {
    if (isMyDrafts) return;
    try {
      const res  = await fetch(`${API.categories}?list`, { credentials: "include" });
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) { console.error("Categories error:", err); }
  }, [isMyDrafts]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // ── Sync filters from URL ─────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ids    = params.getAll("idcategory");
    const newFlag = params.has("new") &&
      (params.get("new") === "" || params.get("new") === "true" || params.get("new") === "1");

    setEmails([]);
    setPage(1);
    setHasMore(true);
    setActiveCategories(ids);
    setIsNew(newFlag);
  }, [location.search]);

  // ── Fetch emails ──────────────────────────────────────────────────────────
  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = isMyDrafts
        ? `${API.emails}?list&myDrafts`
        : `${API.emails}?list`;

      const params = new URLSearchParams(location.search);
      const search = params.get("search") || "";

      const fd = new FormData();
      fd.append("currentPage", page);
      if (search.trim()) fd.append("search", search.trim());
      if (isNew) fd.append("new", "true");
      activeCategories.forEach((id) => fd.append("idcategory[]", id));

      const res  = await fetch(endpoint, { method: "POST", credentials: "include", body: fd });
      const data = await res.json();

      if (!Array.isArray(data) || data.length === 0) {
        if (page === 1) setEmails([]);
        setHasMore(false);
        return;
      }

      if (page === 1) setEmails(data);
      else setEmails((prev) => [...prev, ...data]);
    } catch (err) {
      console.error("Emails error:", err);
      if (page === 1) setEmails([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setButtonDisabled(true);
      setTimeout(() => setButtonDisabled(false), 2000);
    }
  }, [isMyDrafts, location.search, page, isNew, activeCategories]);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  // ── Infinite scroll com IntersectionObserver ──────────────────────────────
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!hasMore || loading || buttonDisabled) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, buttonDisabled]);

  // ── URL helpers ───────────────────────────────────────────────────────────
  const updateUrl = (selectedCategories, newFlag) => {
    const params = new URLSearchParams();
    const search = new URLSearchParams(location.search).get("search");
    if (search) params.set("search", search);
    (selectedCategories || []).forEach((id) => params.append("idcategory", id));
    if (newFlag) params.set("new", "true");
    const q = params.toString();
    navigate(q ? `${location.pathname}?${q}` : location.pathname);
  };

  const handleApplyFilters = (selected) => updateUrl(selected || [], isNew);
  const handleToggleNew    = ()          => updateUrl(activeCategories, !isNew);
  const handleRemove       = (idemail)   => setEmails((prev) => prev.filter((e) => e.idemail !== idemail));

  const handleLoadMore = () => {
    if (!buttonDisabled && !loading && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const placeholders = Array.from({ length: 6 }, (_, i) => (
    <div key={`placeholder-${i}`} className="col-md-6 col-lg-4">
      <div className="card rounded-4 h-100 border animate__animated animate__fadeIn animate__slow">
        <div className="card-header bg-white rounded-top-4 border-0 p-3 pb-0">
          <div className="mb-2">
            <span className="placeholder col-4"></span>
          </div>
          <h5 className="card-title fs-5 mb-0">
            <span className="placeholder col-8"></span>
          </h5>
        </div>
        <div className="card-body">
          <p className="card-text">
            <span className="placeholder col-7"></span><br />
            <span className="placeholder col-5"></span><br />
            <span className="placeholder col-6"></span><br />
            <span className="placeholder col-4"></span><br />
          </p>
        </div>
        <div className="card-footer rounded-bottom-4 bg-transparent p-3">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                type="button"
                className="btn btn-outline-primary disabled placeholder"
                disabled
              >
                Loading...
              </button>
            </div>
            <div className="col text-end">
              <span className="placeholder col-1 me-2"></span>
              <span className="placeholder col-1"></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  ));

  return (
    <AppLayout currentPage="emails">
      <NavEmailMarketing />

      {/* Filters — only for Email Vault */}
      {!isMyDrafts && (
        <Filters
          categories={categories}
          activeCategories={activeCategories}
          onApplyFilters={handleApplyFilters}
          isNew={isNew}
          onToggleNew={handleToggleNew}
          filterTypes={[
            { type: "caption", label: "Content Pillar" },
          ]}
        />
      )}

      {/* Grid */}
      {loading && emails.length === 0 ? (
        <div className="row g-4">
          {placeholders}
        </div>
      ) : emails.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <p>{isMyDrafts ? "No email drafts yet." : "No emails found."}</p>
        </div>
      ) : (
        <div className="row g-4">
          {emails.map((email) => (
            <div key={email.idemail} className="col-md-6 col-lg-4">
              <EmailCard email={email} onRemove={handleRemove} />
            </div>
          ))}
        </div>
      )}

      {hasMore && emails.length > 0 && !loading && (
        <div className="text-center mt-4" ref={sentinelRef}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleLoadMore}
            disabled={buttonDisabled || loading}
          >
            {loading ? "Loading..." : "Load More"}
          </button>
        </div>
      )}

      <EmailPrompt onSaved={fetchEmails} />
    </AppLayout>
  );
}